/* ────────────────────────────────────────────────────────────────────────────
 * LATENT FIELD — the app's signature visual
 * ────────────────────────────────────────────────────────────────────────────
 * A contact-sheet lattice of frame cells. At rest it breathes slowly in the
 * dark; while generating, a play-head sweeps left to right and the cells it
 * passes "resolve" — noise collapsing into a coherent light field. It is the
 * diffusion process made literal, in the shape of a filmstrip.
 * ──────────────────────────────────────────────────────────────────────────── */

import { useEffect, useRef } from 'react';

interface LatentFieldProps {
  /** 0..1 — how far the current job has come. */
  progress: number;
  /** True while a job is running: raises intensity and starts the play-head. */
  active: boolean;
  /** True once a result exists: the field settles into a calm resolved state. */
  settled?: boolean;
  className?: string;
}

const COLS = 26;
const ROWS = 14;

/** Cheap value noise, deterministic per cell. */
function cellNoise(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

export function LatentField({ progress, active, settled = false, className }: LatentFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Live values read inside the rAF loop without restarting it.
  const live = useRef({ progress, active, settled });
  live.current = { progress, active, settled };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion =
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    let width = 0;
    let height = 0;
    const start = performance.now();

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const draw = (now: number) => {
      const t = (now - start) / 1000;
      const { progress: p, active: isActive, settled: isSettled } = live.current;

      ctx.clearRect(0, 0, width, height);

      // Ground wash
      const bg = ctx.createLinearGradient(0, 0, 0, height);
      bg.addColorStop(0, '#05070b');
      bg.addColorStop(1, '#080b11');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const padX = width * 0.045;
      const padY = height * 0.11;
      const gridW = width - padX * 2;
      const gridH = height - padY * 2;
      const cw = gridW / COLS;
      const ch = gridH / ROWS;
      const gap = Math.max(1, Math.min(cw, ch) * 0.13);

      // The play-head position: driven by progress while active, otherwise a
      // slow idle drift so the panel never feels dead.
      const head = isActive ? p : isSettled ? 1 : (Math.sin(t * 0.35) * 0.5 + 0.5) * 0.9 + 0.05;
      const intensity = isActive ? 1 : isSettled ? 0.5 : 0.28;
      const time = reduceMotion ? 0 : t;

      for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
          const cx = padX + col * cw;
          const cy = padY + row * ch;
          const u = col / (COLS - 1);
          const v = row / (ROWS - 1);

          // How resolved this cell is: cells to the left of the head are done,
          // with a ragged edge so the boundary reads organic.
          const ragged = cellNoise(col, row, 1) * 0.13;
          const edge = head - ragged;
          const resolved = Math.max(0, Math.min(1, (edge - u) * 7 + 0.5));

          // Coherent light field (what a "resolved" latent looks like here).
          const wave =
            Math.sin(u * 5.4 + time * 0.75 + v * 2.1) * 0.5 +
            Math.sin(v * 4.1 - time * 0.5 + u * 3.3) * 0.5;
          const field = (wave + 2) / 4; // 0..1

          const noise = cellNoise(col, row, Math.floor(time * 12) % 97);

          // Blend noise → field as the cell resolves.
          const lum = noise * (1 - resolved) + field * resolved;

          // Colour: teal core, violet in the cold corners, warm at the head.
          const nearHead = Math.max(0, 1 - Math.abs(u - head) * 9);
          const r = 24 + lum * 40 + nearHead * 130;
          const g = 60 + lum * 165 + nearHead * 60;
          const b = 70 + lum * 130 + (1 - resolved) * 40;

          const alpha =
            (0.1 + lum * 0.55 * resolved + (1 - resolved) * 0.1 + nearHead * 0.35) * intensity;

          ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${alpha.toFixed(3)})`;
          ctx.fillRect(cx + gap / 2, cy + gap / 2, cw - gap, ch - gap);
        }
      }

      // Play-head bar
      if (isActive || !isSettled) {
        const hx = padX + head * gridW;
        const hg = ctx.createLinearGradient(hx - cw * 2.4, 0, hx + cw * 0.9, 0);
        hg.addColorStop(0, 'rgba(70,230,196,0)');
        hg.addColorStop(1, `rgba(120,255,225,${0.24 * intensity})`);
        ctx.fillStyle = hg;
        ctx.fillRect(hx - cw * 2.4, padY, cw * 3.3, gridH);

        ctx.fillStyle = `rgba(180,255,240,${(isActive ? 0.9 : 0.32).toFixed(2)})`;
        ctx.fillRect(hx, padY - ch * 0.35, 1.4, gridH + ch * 0.7);
      }

      // Filmstrip sprockets, top and bottom
      const sprocketW = cw * 0.42;
      const sprocketH = Math.max(2.5, padY * 0.2);
      ctx.fillStyle = 'rgba(255,255,255,0.11)';
      for (let col = 0; col < COLS; col += 2) {
        const cx = padX + col * cw + (cw - sprocketW) / 2;
        ctx.fillRect(cx, padY * 0.42, sprocketW, sprocketH);
        ctx.fillRect(cx, height - padY * 0.42 - sprocketH, sprocketW, sprocketH);
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden />;
}
